import React, { Suspense } from 'react';
import { type RouteObject } from 'react-router';
import { HasPermission } from '@/components/Permission/HasPermission';
import LoadingCompanyDashboard from '@/components/UI/loading/LoadingCompanyDashboard/index.tsx';
import LoadingSherpaProjectsPage from '@/components/UI/loading/PageSkeletonLoading/LoadingSherpaProjectsPage/index.tsx';
import LoadingSettings from '@/components/UI/loading/Settings/LoadingSettings/index.tsx';
import LoadingVendor from '@/components/UI/loading/Vendor/LoadingVendor/index.tsx';
import Unauthorized from '@/components/Unauthorized';
import type { HasPermissionFn } from '@/hooks/usePermission/index.tsx';
import Administration from '@/pages/Administration/index.tsx';
import Settings from '@/pages/Settings/index.tsx';
import Learn from '../../pages/Learn';
// import { MyApprovals } from "../../pages/MyApprovals";
import { ApprovalDetails } from '../../pages/MyApprovals/ApprovalDetails';
import MyDashboard from '../../pages/MyDashboard';
import PageNotFound from '../../pages/PageNotFound';
import LotteryManagementForbidden from '../../pages/Lottery/LotteryManagementForbidden';
import RequireSubscription from '../RequireSubscription';
import AdministrationRoute from './AdministrationRoute/index.tsx';
import { RoverRoute } from './RoverRoute/index.tsx';
import SettingsRoute from './SettingsRoute/index.tsx';
import { SherpaRoute } from './SherpaRoute';

const MyTasks = React.lazy(() => import('../../pages/MyTasks'));
const Task = React.lazy(() => import('../../pages/MyTasks/Task'));
const MyDeliverables = React.lazy(() => import('../../pages/MyDeliverables'));
const Deliverable = React.lazy(() => import('../../pages/MyDeliverables/Deliverable'));
const MyMilestones = React.lazy(() => import('../../pages/MyMilestones'));
const Milestone = React.lazy(() => import('../../pages/MyMilestones/Milestone'));

const MyApprovals = React.lazy(() => import('../../pages/MyApprovals'));
const Sabacus = React.lazy(() => import('../../pages/Sabacus'));

const Vendor = React.lazy(() => import('../../pages/Vendor'));

const CompanyDashboard = React.lazy(() => import('../../pages/CompanyDashboard'));

export default function AppRoute(hasPermission: HasPermissionFn) {
  const routes = [
    {
      path: 'administration',
      element: <Administration />,
      children: AdministrationRoute(hasPermission),
    },
    {
      path: 'vendor',
      element: (
        <HasPermission of='LIST_VENDORS' fallback={<Unauthorized />}>
          <Suspense fallback={<LoadingVendor />}>
            <Vendor />
          </Suspense>
        </HasPermission>
      ),
    },
    {
      path: 'settings',
      element: (
        <Suspense fallback={<LoadingSettings />}>
          <Settings />
        </Suspense>
      ),
      children: SettingsRoute(),
    },
    { path: 'learn', element: <Learn /> },
    {
      path: 'company',
      element: (
        <Suspense fallback={<LoadingCompanyDashboard />}>
          <CompanyDashboard />
        </Suspense>
      ),
    },
    {
      path: 'dashboard',
      children: [
        {
          index: true,
          element: <MyDashboard />,
        },
        {
          path: 'tasks',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<LoadingCompanyDashboard />}>
                  {' '}
                  <MyTasks />{' '}
                </Suspense>
              ),
            },
            {
              path: ':taskId',
              element: <Task />,
            },
          ],
        },
        {
          path: 'deliverables',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<LoadingCompanyDashboard />}>
                  <MyDeliverables />
                </Suspense>
              ),
            },
            {
              path: ':deliverableId',
              element: <Deliverable />,
            },
          ],
        },
        {
          path: 'milestones',
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<LoadingCompanyDashboard />}>
                  <MyMilestones />
                </Suspense>
              ),
            },
            {
              path: ':milestoneId',
              element: <Milestone />,
            },
          ],
        },
        {
          path: 'approvals',
          children: [
            {
              index: true,
              element: (
                <HasPermission of='LIST_APPROVALS' fallback={<Unauthorized />}>
                  <MyApprovals />
                </HasPermission>
              ),
            },
            {
              path: ':approvalId',
              element: (
                <HasPermission of='LIST_APPROVALS' fallback={<Unauthorized />}>
                  <ApprovalDetails />
                </HasPermission>
              ),
            },
          ],
        },
      ],
    },
    {
      path: 'sherpa',
      element: (
        <Suspense fallback={<LoadingSherpaProjectsPage />}>
          <RequireSubscription subscription='SHERPA' />
        </Suspense>
      ),
      children: SherpaRoute(hasPermission),
    },
    {
      path: 'rover',
      element: <RequireSubscription subscription='ROVER' />,
      children: RoverRoute(hasPermission),
    },
    {
      path: 'lottery/*',
      element: <LotteryManagementForbidden />,
    },
    {
      path: 'sabacus',
      element: <Sabacus />,
    },
    { path: '*', element: <PageNotFound /> },
  ] as RouteObject[];

  return routes;
}
